/**
 * Adapters between AllergyForm's Allergy v2 body shape (camelCase, sent
 * to api-allergy-v2-service today) and the transcript's captured-item
 * payload shape (snake_case, matches what the Python agent emits and
 * what Go's SaveSection fan-out reads on Save & next).
 *
 * Same pattern as complaintBodyAdapters / diagnosisBodyAdapters /
 * medicineBodyAdapters / hospitalisationBodyAdapters /
 * labInvestigationBodyAdapters.
 *
 *   Allergy v2 add body      → captured "add" payload
 *   {name, category, catalogId, mCode, source,
 *    reactions:[{name, source}], firstNoted, lastReaction?,
 *    description}
 *      →
 *   {substance, name, category, reactions, since_date, description}
 *
 * Simplifications for the transcript flow:
 *   - The AI-side `add_allergy` tool only carries `substance`, `category`
 *     (Salt|Food|Other), `reactions` (comma string), `since_date`,
 *     `description`. It never resolves a catalog id / mCode, so those
 *     stay blank on the round-trip; the Go adapter forces
 *     `source="free-text"` on the wire.
 *   - Reactions are round-tripped as an array of {name, source} objects.
 *     The AI's comma-string is normalised into the same shape at seed
 *     time by AllergyForm (seedReactionsFromPayload), so the review
 *     board sees consistent chips regardless of input path.
 */

const toIsoDate = (iso) => (typeof iso === "string" && iso ? iso.slice(0, 10) : "");

/**
 * Allergy v2 add body → captured "add" payload.
 */
export function bodyToCapturedAdd(body) {
  const rxs = Array.isArray(body.reactions) ? body.reactions : [];
  return {
    substance:   body.name || "",
    name:        body.name || "",
    category:    body.category || "Other",
    reactions:   rxs.map((r) => ({
      name:   (r && r.name) || "",
      source: (r && r.source) || "free-text",
    })).filter((r) => r.name),
    since_date:  toIsoDate(body.firstNoted),
    description: body.description || "",
  };
}

/**
 * Stub — Wave B first-cut doesn't route Allergy updates via AI. Symmetric
 * with the CC/Dx/Med/Hosp/Lab adapters. Kept for parity with the
 * pass-2 hook and so ReviewSave's click-to-edit re-open path can reuse
 * the same payload shape.
 */
export function bodyToCapturedUpdate(body /* , context */) {
  return bodyToCapturedAdd(body);
}
